import { useEffect, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";

import {
  formatPlaylistClip,
  getPlayerName,
  getMatchType,
  getPlayerPhoto,
  getTeamLogo,
  getCountryCode,
} from "./Utilities";
import {
  PlaylistClipsState,
  ActivePlaylistClipState,
  TeamLogoState,
  PlayerPhotoState,
  FallbackPlaylistTypeState,
  PlayerNameState,
  MatchTypeState,
  DisplayEmptyState,
  FallbackPlaylistState,
  TopViewedPlayersState,
  TopViewedMatchesState,
  TopViewedTeamsState,
  RenderPreMatchState,
  TopViewedPlayersByCountryState,
} from "MVVM/State/embeddablePlaylist";
import { PlaylistClip } from "./@types";

export type TagItem = {
  key: string;
  value: string;
  clips: PlaylistClip[];
};

export type TagClipMap = TagItem[];

const filterActiveClips = (clips: PlaylistClip[]): PlaylistClip[] => {
  return clips.filter((clip: PlaylistClip) => {
    const status = clip.status?.toLowerCase();
    return status !== "deleted" && status !== "inactive";
  });
};

const setFilteredPlaylistClips = (
  clips: PlaylistClip[],
  setPlaylistClips: (clips: PlaylistClip[]) => void,
): void => {
  const filteredClips = filterActiveClips(clips);
  setPlaylistClips(filteredClips);
};

const formatMetadataValue = (
  key: string,
  value: string,
  game?: string,
): string => {
  if (game === "CS2" || !game) {
    if (key === "Kill Count") {
      return `${value}k`;
    }
  }
  return value;
};

const shouldIncludeAsTag = (key: string, value: string): boolean => {
  const excludedKeys = [
    "matchId",
    "eventId",
    "eventName",
    "eventLogo",
    "playerId",
    "playerName",
    "playerPhoto",
    "teamId",
    "teamName",
    "teamLogo",
  ];

  const allowedValues = [
    "Ace",
    "1V1",
    "1V2",
    "1V3",
    "1V4",
    "1V5",
    "4K",
    "AWP",
    "Wallbang",
  ];

  if (!value || excludedKeys.includes(key)) {
    return false;
  }

  return allowedValues.includes(value);
};

type PlaylistFetchParams = {
  playlist: string;
  platform: string;
};

export const EmbeddablePlaylistViewModel = ({
  playlist,
  platform,
}: PlaylistFetchParams) => {
  const [playlistClips, setPlaylistClips] = useRecoilState(PlaylistClipsState);
  const [filters, setFilters] = useState<TagClipMap>([]);
  const [activePlaylistClip, setActivePlaylistClip] = useRecoilState(
    ActivePlaylistClipState,
  );

  const setPlayerPhoto = useSetRecoilState(PlayerPhotoState);
  const setTeamLogo = useSetRecoilState(TeamLogoState);
  const setPlayerName = useSetRecoilState(PlayerNameState);
  const setFallbackPlaylistType = useSetRecoilState(FallbackPlaylistTypeState);
  const [displayEmptyState, setDisplayEmptyState] =
    useRecoilState(DisplayEmptyState);
  const setTopViewedPlayers = useSetRecoilState(TopViewedPlayersState);
  const setTopViewedPlayersByCountry = useSetRecoilState(
    TopViewedPlayersByCountryState,
  );
  const setTopViewedMatches = useSetRecoilState(TopViewedMatchesState);
  const setTopViewedTeams = useSetRecoilState(TopViewedTeamsState);
  const setRenderPreMatchState = useSetRecoilState(RenderPreMatchState);
  const searchParams = new URLSearchParams(window.location.search);
  const renderTopViewedList = searchParams.get("topViewedList");

  useEffect(() => {
    const encodedPlatform = encodeURIComponent(platform);
    const clipUrl = `${process.env.REACT_APP_PARTNER_URL}/playlist?name=${playlist}&platform=${encodedPlatform}&fmt=iframe`;
    async function fetchData() {
      const res = await fetch(clipUrl, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();

      if (!data?.data?.clips?.length) {
        fetchTrendingContent();
        setDisplayEmptyState(true);
        return;
      } else if (renderTopViewedList) {
        fetchTrendingContent();
      }

      const firstClip = data.data.clips[0];
      const photo = getPlayerPhoto(firstClip);
      const logo = getTeamLogo(firstClip);
      const playerName = getPlayerName(firstClip);
      const formattedClip = formatPlaylistClip(firstClip);
      const isFallback = data.fallback;
      if (isFallback && ["recentBest", ""].includes(data.fallbackType)) {
        setDisplayEmptyState(true);
        fetchTrendingContent();
      } else if (
        isFallback &&
        ["lastMatch", "teamClips"].includes(data.fallbackType)
      ) {
        setRenderPreMatchState(true);
        fetchTrendingContent();
        setActivePlaylistClip(formattedClip);
      }

      setPlayerPhoto(photo || "");
      setTeamLogo(logo || "");
      setPlayerName(playerName || "");
      setFallbackPlaylistType(data.fallbackType);

      if (!isFallback) {
        setActivePlaylistClip(formattedClip);
      }
      setFilteredPlaylistClips(data.data.clips, setPlaylistClips);
    }

    const baseUrl = `${process.env.REACT_APP_PARTNER_URL}/cs/match/hltv/topList`;
    async function fetchTrendingContent() {
      const countryCode = getCountryCode();

      const trendingContentUrl = countryCode
        ? `${baseUrl}?country=${countryCode}`
        : baseUrl;
      const res = await fetch(trendingContentUrl, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();

      setTopViewedPlayers(data.players);
      setTopViewedPlayersByCountry(data.playersByCountry);
      setTopViewedMatches(data.matches);
      setTopViewedTeams(data.teams);
    }

    fetchData();
  }, [
    platform,
    playlist,
    setActivePlaylistClip,
    setFallbackPlaylistType,
    setPlayerName,
    setPlayerPhoto,
    setPlaylistClips,
    setTeamLogo,
    setDisplayEmptyState,
    setTopViewedMatches,
    setTopViewedPlayers,
    setTopViewedTeams,
    renderTopViewedList,
    setRenderPreMatchState,
    setTopViewedPlayersByCountry,
  ]);

  useEffect(() => {
    const tagMap: { [uniqueKey: string]: TagItem } = {};
    if (Array.isArray(playlistClips)) {
      for (const clip of playlistClips) {
        if (!clip || !clip.metadata) continue;
        for (const meta of clip.metadata) {
          if (
            typeof meta === "object" &&
            meta !== null &&
            "key" in meta &&
            "value" in meta
          ) {
            const key = (meta as { key: string; value: string }).key;
            const value = (meta as { key: string; value: string }).value;
            if (
              !value ||
              key === "matchId" ||
              key === "eventId" ||
              key === "eventName" ||
              key === "eventLogo" ||
              key === "playerId" ||
              key === "playerName" ||
              key === "playerPhoto" ||
              key === "teamId" ||
              key === "teamName" ||
              key === "teamLogo"
            )
              continue;

            if (!shouldIncludeAsTag(key, value)) {
              continue;
            }

            const displayValue = formatMetadataValue(key, value, "CS2");
            const uniqueKey = `${key}:${value}`;

            if (!tagMap[uniqueKey]) {
              tagMap[uniqueKey] = {
                key,
                value: displayValue,
                clips: [],
              };
            }
            tagMap[uniqueKey].clips.push(clip);
          }
        }
      }
      const tagsArray = Object.values(tagMap);
      setFilters(tagsArray);
    }
  }, [playlistClips]);

  useEffect(() => {
    if (activePlaylistClip) {
      const photo = getPlayerPhoto(activePlaylistClip);
      const logo = getTeamLogo(activePlaylistClip);
      const playerName = getPlayerName(activePlaylistClip);
      setPlayerPhoto(photo || "");
      setTeamLogo(logo || "");
      setPlayerName(playerName || "");
    }
  }, [activePlaylistClip, setPlayerPhoto, setTeamLogo, setPlayerName]);

  const getClipsForTag = (tagKey: string): PlaylistClip[] => {
    if (tagKey === "all") {
      return playlistClips || [];
    }

    const tag = filters.find((tag) => `${tag.key}:${tag.value}` === tagKey);
    return tag?.clips || [];
  };

  return {
    filters,
    getClipsForTag,
  };
};

export const LeagueEmbeddablePlaylistViewModel = ({
  playlist,
  platform,
}: PlaylistFetchParams) => {
  const setPlaylistClips = useSetRecoilState(PlaylistClipsState);
  const setActivePlaylistClip = useSetRecoilState(ActivePlaylistClipState);

  useEffect(() => {
    const encodedPlatform = encodeURIComponent(platform);
    const url = `${process.env.REACT_APP_PARTNER_URL}/playlist?name=${playlist}&platform=${encodedPlatform}&fmt=iframe`;
    async function fetchData() {
      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();

      if (!data?.data?.clips?.length) return;

      const firstClip = data.data.clips[0];
      const formattedClip = formatPlaylistClip(firstClip);

      setActivePlaylistClip(formattedClip);
      setFilteredPlaylistClips(data.data.clips, setPlaylistClips);
    }

    fetchData();
  }, [playlist, setPlaylistClips, setActivePlaylistClip, platform]);
};

export const NextUpEmbeddablePlaylistViewModel = ({
  playlist,
  platform,
}: PlaylistFetchParams) => {
  const setPlaylistClips = useSetRecoilState(PlaylistClipsState);
  const setActivePlaylistClip = useSetRecoilState(ActivePlaylistClipState);
  const setMatchType = useSetRecoilState(MatchTypeState);
  const setIsFallbackPlaylist = useSetRecoilState(FallbackPlaylistState);

  useEffect(() => {
    const encodedPlatform = encodeURIComponent(platform);
    const url = `${process.env.REACT_APP_PARTNER_URL}/playlist?name=${playlist}&platform=${encodedPlatform}&fmt=iframe`;
    async function fetchData() {
      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();

      if (!data?.data?.clips?.length) return;

      setIsFallbackPlaylist(data.fallback);

      const firstClip = data.data.clips[0];
      const clipMatchType = getMatchType(firstClip);

      setMatchType(clipMatchType || "");

      const formattedClip = formatPlaylistClip(firstClip);

      setActivePlaylistClip(formattedClip);
      setFilteredPlaylistClips(data.data.clips, setPlaylistClips);
    }

    fetchData();
  }, [
    playlist,
    setPlaylistClips,
    setActivePlaylistClip,
    platform,
    setMatchType,
    setIsFallbackPlaylist,
  ]);
};
